<script setup lang="ts">
import { useFlag } from '@unleash/proxy-client-vue'
import { ElNotification } from 'element-plus'
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import { useGetEmailQuota } from '@/api/email-quota.api'
import HelpOldIcon from '@/assets/icons/help-old.svg?component'
import IconClose from '@/assets/icons/remove.svg?component'
import TagsList from '@/components/CampaignForm/components/CampaignFormAudience/TagsList.vue'
import InviteUserLink from '@/components/InviteUserLink.vue'
import { CampaignRepository } from '@/legacy/repositories/campaignRepository'
import { FLAG_EMAIL_QUOTA } from '@/lib/constants'
import { filterInvitedUsers } from '@/lib/helpers/campaign.helpers'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useAuthStore } from '@/stores/auth.stores'
import type { InvitedUserType } from '@/types'
import type { UserTag } from '@/types/userTags'
import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ campaignToken: string; campaignId: string; userTags: UserTag[] }>()
const emits = defineEmits(['onInvite', 'onClose'])
const store = useStore()
const isInviteUsersModalOpen = defineModel<boolean>('isInviteUsersModalOpen', { default: false })
const isQuotaEnabled = useFlag(FLAG_EMAIL_QUOTA)
const advertiserStore = useAdvertiserStore()
const authStore = useAuthStore()
const { quota, isLoading: isQuotaLoading, getEmailsQuota } = useGetEmailQuota()
const onboardingStep = computed(() => store.getters['onboarding/currentOnboardingStep'])

const quotaText = computed(() => {
  if (quota.value === 0) {
    return 'No invitations left'
  }
  if (quota.value === 1) {
    return '1 invitation email left this week'
  }
  return `${quota.value} invitation emails left this week`
})

const formData = reactive<{ tagIds: number[]; invitedUsers: InvitedUserType[] }>({
  tagIds: [],
  invitedUsers: [],
})

const isSendStatusLoading = ref<boolean>(false)
const sendInvites = async () => {
  try {
    isSendStatusLoading.value = true
    const { data: targets } = await CampaignRepository.getTargets(props.campaignId, authStore.authorization)
    const mergedTargets = [...targets.user_tag_ids, ...formData.tagIds.filter((tag) => !targets.user_tag_ids.includes(tag))]
    const mergedEmails = [
      ...targets.emails,
      ...filterInvitedUsers(formData.invitedUsers).filter((email) => !targets.emails.includes(email)),
    ]
    await CampaignRepository.updateCampaignUsersInvite(
      // TODO: legacy way of adding campaign users through store
      {
        ...targets,
        ...{ emails: mergedEmails },
        ...{ user_tag_ids: mergedTargets },
      },
      props.campaignId,
      authStore.authorization,
    )
    if (isQuotaEnabled.value && advertiserStore?.advertiser) {
      await getEmailsQuota(advertiserStore?.advertiser.id)
    }
    formData.invitedUsers = []
    formData.tagIds = []
    emits('onInvite')
  } catch {
    ElNotification({
      title: 'Something went wrong',
      message: 'Please try again',
      type: 'error',
    })
  }
  isSendStatusLoading.value = false
}

onMounted(async () => {
  if (advertiserStore.advertiser?.id && isQuotaEnabled.value) {
    await getEmailsQuota(advertiserStore.advertiser.id)
  }
  if (onboardingStep.value === 3) {
    await store.dispatch('onboarding/setCurrentCard', 'manage-campaign-invite-creators-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
})
</script>

<template>
  <el-dialog v-model="isInviteUsersModalOpen" :show-close="false" @close="() => emits('onClose')">
    <template #header="{ close }">
      <FlexComponent justify="space-between">
        <TypographyComponent variant="headline-medium">Invite</TypographyComponent>
        <IconClose class="close-btn" @click="close" />
      </FlexComponent>
    </template>

    <BoxComponent id="manage-campaign-invite-creators-section" class="invite-container" :mt="24" :mb="24">
      <BoxComponent :mb="8">
        <TypographyComponent variant="title-large"> Invite with link </TypographyComponent>
      </BoxComponent>
      <InviteUserLink :campaign-token="campaignToken" />
    </BoxComponent>

    <BoxComponent :mb="24">
      <BoxComponent :mb="16">
        <TypographyComponent variant="title-large">Invite creators by email</TypographyComponent>
      </BoxComponent>
      <campaign-form-invite-users v-model:invited-users="formData.invitedUsers" />
    </BoxComponent>

    <BoxComponent v-if="userTags.length" :mb="24">
      <BoxComponent :mb="16">
        <TypographyComponent variant="title-large">Invite creator tags</TypographyComponent>
      </BoxComponent>
      <div class="tag-container">
        <TagsList v-model:tag-ids="formData.tagIds" :user-tags="userTags" />
      </div>
    </BoxComponent>

    <template #footer>
      <FlexComponent justify="end" :gap="16">
        <el-tooltip v-if="isQuotaEnabled" placement="bottom-start" effect="light" :show-arrow="false">
          <BoxComponent v-if="isQuotaLoading">
            <LoadingComponent v-if="true" :size="24" />
          </BoxComponent>
          <FlexComponent v-else align="center" :gap="8">
            <TypographyComponent variant="body-medium">{{ quotaText }}</TypographyComponent>
            <el-icon :size="24">
              <HelpOldIcon />
            </el-icon>
          </FlexComponent>
          <template #content>
            <div class="add-user-dialog__tooltip-content">
              <TypographyComponent variant="title-medium">Invitation emails</TypographyComponent>
              <TypographyComponent variant="body-medium"
                >Inviting creators by email sends them an invitation. To avoid spam issues, there's a weekly limit of 50 emails. You can
                still invite unlimited creators using the invite link.</TypographyComponent
              >
            </div>
          </template>
        </el-tooltip>
        <ButtonComponent
          variant="accent"
          :is-loading="isSendStatusLoading"
          :is-disabled="!formData.tagIds.length && !formData.invitedUsers.length"
          @click="sendInvites"
          >Invite</ButtonComponent
        >
      </FlexComponent>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.close-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.tag-container {
  max-height: 220px;
  overflow: auto;
}

.invite-container {
  border-radius: 16px;
  background: rgba(3, 30, 81, 0.08);
  padding: 24px;
}
.add-user-dialog__tooltip-content {
  max-width: 280px;
}
</style>
